/**
 * QEE chat text input.
 * Developers: Adrian Engelmann, Kiran Jayaramu, Nicu Bratescu, Daniela Popescu
 * Copyright 2026 by Qalgo GmbH (www.qalgo.de). All rights reserved.
 */
import React, { useState, useCallback, useEffect } from 'react';
import { Box, IconButton, TextField, Typography } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import QeeFileInput from '../../../../qeeFileInput';
import QeeTooltip from '../../../../qeeTooltip';

export function MuiTextInput({
	chatController,
	actionRequest,
	sendButtonText: configuredSendButtonText='',
	placeholder: configuredPlaceholder=''
}) {
	const maxLength = 1024;
	const showLengthHint = 128;
	const chatCtl = chatController;
	const initialDefaultValue = actionRequest.defaultValue || '';
	const [value, setValue] = useState(initialDefaultValue);
	const [charCount, setCharCount] = useState(initialDefaultValue.length);
	const [files, setFiles] = useState([]);
	const [uploadId, setUploadId] = useState('');

	const setResponse = useCallback(() => {
		if (value) {
			const res = {
				type: 'text',
				value,
			};
			chatCtl.setActionResponse(actionRequest, res);
			setValue('');
			setCharCount(0);
		}
	}, [actionRequest, chatCtl, value]);

	const handleKeyDown = useCallback((e) => {
		if (chatController.chatConfig?.isP2P) return;

		if (e.nativeEvent.isComposing) {
			return;
		}

		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			setResponse();
		}
	}, [setResponse]);

	const handleChange = (e) => {
		if (e.target.value.length <= maxLength) {
			setValue(e.target.value);
			setCharCount(e.target.value.length);
		}
	};

	const handleFileListReady = (fileArray, newUploadId) => {
		if (fileArray.length > 0) {
			const value = fileArray.map((f) => f.name).toString();
			const res = {
				type: 'file',
				value,
				files: fileArray,
				uploadId: newUploadId // Add uploadId to the response
			};
			chatCtl.setActionResponse(actionRequest, res);
			setFiles([]);
		}
	};

	const initiateFileUpload = () => {
		//const newUploadId = `${Math.floor(Date.now() / 2000)}`; // Generate uploadId with 2 seconds precision
		const newUploadId = `${Date.now()}-${Math.floor(Math.random() * 1000)}`;
		setUploadId(newUploadId);
		return newUploadId;
	};

	useEffect(() => {
		if (chatController.chatConfig?.isP2P) {
			initiateFileUpload();
		}
	}, [chatController.chatConfig?.isP2P, chatCtl.state.messages.length]);

	const sendButtonText = actionRequest.sendButtonText || configuredSendButtonText;

	return (
		<Box
			sx={{
				display: 'flex',
				alignItems: 'flex-end',
				position: 'relative',
				mx: '5px',
				mb: { xs: 0.5, sm: 0.75 },
				px: 1,
				py: 0.75,
				border: '1px solid',
				borderColor: 'divider',
				borderRadius: '26px',
				bgcolor: 'background.paper',
				boxShadow: '0 2px 10px rgba(0,0,0,0.10)',
				'&:focus-within': { borderColor: 'text.secondary' },
				'& > *': { minWidth: 0 }
			}}
		>
			{chatController.chatConfig?.isP2P && (
				<>
					<QeeFileInput 
						facId={chatCtl.parentForm + '-' + chatCtl.parentId}
						buttonText=""
						sdoc_type={'chat'}
						sdoc_subtype={'msg'}
						upload_id={uploadId} // Pass the generated uploadId here
						hideUploadButton={false}
						hideFilesList={true}
						onSuccess={(fileArray) => handleFileListReady(fileArray, uploadId)} // Pass the same uploadId to handleFileListReady
					/>
				</>
			)}
			<TextField
				placeholder={actionRequest.placeholder || configuredPlaceholder}
				value={value}
				onChange={handleChange}
				autoFocus
				multiline
				inputProps={{ onKeyDown: handleKeyDown }}
				variant="outlined"
				maxRows={10}
				sx={{
					flexGrow: 1,
					'& .MuiOutlinedInput-root': {
						p: 0,
						borderRadius: 0,
						'& fieldset': { border: 'none' }
					},
					'& .MuiInputBase-input': {
						px: 1,
						py: 0.75,
						lineHeight: 1.45,
						'@media (max-width:599.95px) and (hover:none) and (pointer:coarse)': {
							fontSize: '1.25rem'
						}
					}
				}}
			/>
			{chatController.chatConfig?.isP2P && charCount > showLengthHint &&
				<Typography
					variant="caption"
					sx={{
						position: 'absolute',
						top: 0,
						left: 10,
						color: 'text.secondary',
					}}
				>
					{`${charCount}/${maxLength}`}
				</Typography>
			}
			<QeeTooltip title={sendButtonText}>
				<span>
					<IconButton
						className="qeeChatSendButton"
						aria-label={sendButtonText}
						disabled={!value && files.length === 0}
						onClick={files.length > 0 ? () => handleFileListReady(files, uploadId) : setResponse}
						sx={{
							width: 36,
							height: 36,
							mb: 0.1,
							backgroundColor: 'var(--primary-color, #004c3f) !important',
							color: '#fff !important',
							'& .MuiSvgIcon-root': {
								color: '#fff !important'
							},
							'&:hover': {
								backgroundColor: 'var(--primary-color, #004c3f) !important',
								filter: 'brightness(0.88)'
							},
							'&.Mui-disabled': {
								backgroundColor: '#d7dfdd !important',
								color: '#6f7b78 !important',
								filter: 'none',
								opacity: 1,
								'& .MuiSvgIcon-root': {
									color: '#6f7b78 !important'
								}
							}
						}}
					>
						<ArrowUpwardIcon fontSize="small" />
					</IconButton>
				</span>
			</QeeTooltip>
		</Box>
	);
}
